/*
P17001 - Aconex UI Auth
https://redgum.atlassian.net/wiki/spaces/ONETRACK/pages/3111878663/P17001+-+Aconex+UI+Auth
*/
import {
  AconexImportDialogParams,
  AconexImportDialogResult,
} from '@/components/documents/aconex-import-dialog/aconex-import-dialog-models';
import { SnackbarItem, SnackbarTypeEnum } from '@/components/global/snackbar/snackbar-models';
import { vxm } from '@/store';
import OtApi, { executeApi } from './api.service';
import { PostAuthorizationCodeRequestModel } from './generated/api';

export const ACONEX_AUTH_WINDOW_MESSAGE_NAME = 'aconexAuthComplete';
export type AconexAuthCompleteMessageData = {
  name: typeof ACONEX_AUTH_WINDOW_MESSAGE_NAME;
  data: {
    code: string;
  };
};

// The IDP sends messages to the window all the time, we need to not log about them
// Like, seriously, every 10 seconds or so.
const rawOtherOriginsIgnoreList = process.env.VUE_APP_ACONEX_AUTH_OTHER_ORIGINS_IGNORE_LIST || '[]';
const OTHER_ORIGINS_IGNORE_LIST = new Set(JSON.parse(rawOtherOriginsIgnoreList));

export const doAconexAuth = () => {
  return new Promise<boolean>((resolve, reject) => {
    // We're not going to check if we're already connecting, we'll just set the flag and let it run
    vxm.aconex.setIsBusy(true);
    vxm.aconex.setMessage('Connecting to Aconex...');

    let gotMessage = false;
    // Yeah, nah, we need this as a variable here, so we can access it inside the message handler
    // if we do a const down at the window.open, we can't access the variable yet in the callback (or, that's what the compiler is telling me)
    // So, we have to delcare it and assign it down the bottom
    // eslint-disable-next-line prefer-const
    let popup: ReturnType<typeof window.open>;

    const messageHandler = async (event?: MessageEvent) => {
      // gotta have an event
      if (!event) {
        console.error('aconex_auth_message_handler: event is falsy, bailing');
        return;
      }
      // gotta have the right origin
      if (event.origin !== window.location.origin) {
        if (!OTHER_ORIGINS_IGNORE_LIST.has(event.origin)) {
          console.warn(
            `aconex_auth_message_handler: wrong origin, bailing. Expected:"${window.location.origin}" got:"${event.origin}"`,
            event,
          );
        }
        return;
      }
      // MessageEvent is actually a generic type, but only since typescript v4
      // https://github.com/microsoft/TypeScript-DOM-lib-generator/pull/860
      // at the time of writing, we're on v3.9.10
      // so this is a bit of a hack to get the data typed
      const data = event.data as AconexAuthCompleteMessageData;
      // gotta have the right name
      if (data.name !== ACONEX_AUTH_WINDOW_MESSAGE_NAME) {
        console.warn(
          `aconex_auth_message_handler: event.data has wrong name, bailing. Expected:"${ACONEX_AUTH_WINDOW_MESSAGE_NAME}" got:"${data.name}". This is not neccessarily a bad thing, if we are sending messages from elsewhere in the system back to ourselves. This is just not the message we are looking for at aconex auth time`,
          event.data,
        );
        return;
      }
      // gotta have some data
      if (!data.data.code) {
        console.error('aconex_auth_message_handler: event.data.data.code is falsy, bailing', event.data);
        return;
      }
      console.log('aconex_auth_message_handler: set gotMessage');
      // We're done listening for now, remove the handler. Which is us. This is weird but it works.
      window.removeEventListener('message', messageHandler);
      vxm.aconex.setMessage('Getting Aconex Access Token...');
      const api = new OtApi();
      // I don't think we need to wrap this up in the api helper. It'll throw errors if it doesn't work, we should handle them here if and when they happen
      const request = new PostAuthorizationCodeRequestModel({ authorizationCode: data.data.code });
      await api.aconex().postAuthorizationCode(request);
      // now that we've sent stuff to the server, reload the user profile, which has aconex user info on it
      vxm.aconex.setMessage('Reloading User Profile...');
      await vxm.userProfile.getUserProfile({ forceReload: true });
      // and we're done done. The calling code might think we're done because the popup closed, but we're not, and we needed to keep the overlay up for a smidge longer
      vxm.aconex.setIsBusy(false);
      vxm.aconex.setMessage('');
      // And set this flag so we know Good Things happened and we can return true out to the caller
      gotMessage = true;
      popup?.close();
    };

    window.addEventListener('message', messageHandler);
    // DOCO https://help.aconex.com/DisplayContent/implement-smart-construction-platform-oauth#CORE599A8B9EF4174182A54461AF2820D29E_CORE6B5824F2327B42BAB3411C15FD12F7FF
    // <LOBBY>/auth/authorize?response_type=code&client_id=<CLIENT_ID>&redirect_uri=<REDIRECT_URL>
    // VUE_APP_ACONEX_REDIRECT_URL has a slash at the start already, so no need to add it here
    const redirectUri = encodeURIComponent(`${window.location.origin}${process.env.VUE_APP_ACONEX_REDIRECT_URL}`);
    // console.log('aconex_auth: redirectUri', redirectUri);
    const lobbyUrl = `${process.env.VUE_APP_ACONEX_LOBBY_URL}?client_id=${process.env.VUE_APP_ACONEX_CLIENT_ID}&response_type=code&redirect_uri=${redirectUri}`;
    popup = window.open(lobbyUrl, '_blank', 'popup=true,width=600,height=700');

    // there's no event we can listen to for "popup closed"
    // if it was all same origin, we could, but there's some cross orgin in here too, which means we can't attach anything
    // so, we have to poll. Ugh
    const pollInterval = setInterval(() => {
      // if (gotMessage) {
      //   console.log('aconex_auth: got the message, closing the popup');
      //   clearInterval(pollInterval);
      //   popup?.close();
      //   resolve(gotMessage);
      // }
      if (popup?.closed) {
        console.log('aconex_auth: popup closed');
        clearInterval(pollInterval);
        resolve(gotMessage);
      }
    }, 200);
  });
};

export const doAconexImport = async (params: AconexImportDialogParams) => {
  vxm.aconex.setIsBusy(true);
  vxm.aconex.setMessage('Checking Aconex Access...');

  // HACK leave it up for 10 seconds to check styling
  // await new Promise(resolve => setTimeout(resolve, 10_000));

  const api = new OtApi();
  const tokenResult = await executeApi(
    () => api.aconex().getAccessTokenValidity(),
    'Getting Aconex Access Token Validity',
  );

  if (!tokenResult.success || !tokenResult.data) {
    console.error('aconex_import: Unable to get Aconex Access Token Validity. Bailing', tokenResult);
    const snackbarError = new SnackbarItem({
      type: SnackbarTypeEnum.Error,
      message: `Unable to get Aconex Access Token Validity`,
    });
    vxm.snackbar.addToSnackbarQueue(snackbarError);
    vxm.aconex.setIsBusy(false);
    vxm.aconex.setMessage('');
    return new AconexImportDialogResult({ didImport: false });
  }

  return new Promise<AconexImportDialogResult>((resolve, reject) => {
    const openDialog = () => {
      vxm.aconex.setIsBusy(false);
      vxm.aconex.setMessage('');
      vxm.modal
        .openAconexImportDialog(params)
        .then(result => {
          resolve(result);
        })
        .catch(error => {
          console.error('aconex_import: Error doing Aconex Import Dialog. Bailing', error);
          // dunno if we should reject here or not
          resolve(new AconexImportDialogResult({ didImport: false }));
        });
    };
    if (tokenResult.data?.hasAconexInformation && tokenResult.data?.isAconexAccessTokenValid) {
      // we can go straight to the opening of the dialog
      openDialog();
    } else {
      // we need to get the user to authenticate before we can open the dialog
      doAconexAuth()
        .then(didAuth => {
          if (didAuth) {
            // go onto the dialog
            openDialog();
          } else {
            // did not auth, so we're done
            vxm.aconex.setIsBusy(false);
            vxm.aconex.setMessage('');
            resolve(new AconexImportDialogResult({ didImport: false }));
          }
        })
        .catch(error => {
          console.error('aconex_import: Error doing Aconex Auth. Bailing', error);
          // dunno if we should reject here or not
          vxm.aconex.setIsBusy(false);
          vxm.aconex.setMessage('');
          resolve(new AconexImportDialogResult({ didImport: false }));
        });
    }
  });
};
